/**
 * Data Profiler — Phase 1 of the Classification Engine Pipeline
 *
 * The statistical router: receives a raw array of numeric values from a
 * single metric and returns a profile object that tells the break calculator
 * which transform path to use.
 *
 * Every function in this module is a pure function with no side effects
 * and no DOM access. This is the most critical routing decision in the
 * pipeline — if it routes wrong, every map downstream lies.
 *
 * Routing precedence (deterministic decision tree):
 *   1. Divergent check FIRST — structural property (min < 0 AND max > 0)
 *   2. Sparse check — n < requestedClasses
 *   3. Degenerate check — all values identical (min === max)
 *   4. Heavy-tail check — γ₁ > 2.0 OR β₂ > 7.0
 *   5. Default — linearQuantile (bounded/normal data)
 *
 * Sources:
 *   - Grekousis Section 2.2.4 (pp. 71–72): Skewness
 *   - Grekousis Section 2.2.5 (pp. 72–75): Variability
 *   - Grekousis Section 2.2.6 (pp. 75–76): Quantiles
 *   - Gemini report pp. 2–3: Profiling and routing matrix
 */

// ═══════════════════════════════════════════════════════════════════
// STATISTICAL FUNCTIONS — Pure, testable, no side effects
// ═══════════════════════════════════════════════════════════════════

/**
 * Arithmetic mean.
 * @param {number[]} arr - Non-empty array of valid numbers
 * @returns {number}
 */
export function mean(arr) {
  if (arr.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < arr.length; i++) {
    sum += arr[i];
  }
  return sum / arr.length;
}

/**
 * Median (middle value of sorted array, interpolated for even n).
 * @param {number[]} arr - Non-empty array of valid numbers
 * @returns {number}
 */
export function median(arr) {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

/**
 * Sample standard deviation (Bessel's correction, divide by n-1).
 * @param {number[]} arr - Array with at least 2 values
 * @returns {number}
 */
export function stddev(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  let sumSq = 0;
  for (let i = 0; i < arr.length; i++) {
    const diff = arr[i] - m;
    sumSq += diff * diff;
  }
  return Math.sqrt(sumSq / (arr.length - 1));
}

/**
 * Fisher-Pearson skewness with finite-sample correction.
 *
 * Formula: γ₁ = [n / ((n-1)(n-2))] * Σ[(xᵢ - x̄) / s]³
 *
 * Guard: returns 0 for n < 3 (skewness undefined) and flags degenerate.
 *
 * Source: Grekousis Section 2.2.4 (pp. 71–72); Gemini report p. 2.
 *
 * @param {number[]} arr - Array of valid numbers
 * @returns {number} Skewness coefficient
 */
export function skewness(arr) {
  const n = arr.length;
  if (n < 3) return 0;
  const m = mean(arr);
  const s = stddev(arr);
  if (s === 0) return 0; // all identical → no skew
  const correction = n / ((n - 1) * (n - 2));
  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += ((arr[i] - m) / s) ** 3;
  }
  return correction * sum;
}

/**
 * Excess kurtosis with finite-sample correction.
 *
 * Formula: β₂ = [n(n+1) / ((n-1)(n-2)(n-3))] * Σ[(xᵢ - x̄) / s]⁴
 *               - [3(n-1)² / ((n-2)(n-3))]
 *
 * Returns 0 for a normal distribution (excess kurtosis, not raw).
 * Guard: returns 0 for n < 4 (kurtosis undefined) and flags degenerate.
 *
 * Source: Gemini report p. 2.
 *
 * @param {number[]} arr - Array of valid numbers
 * @returns {number} Excess kurtosis
 */
export function kurtosis(arr) {
  const n = arr.length;
  if (n < 4) return 0;
  const m = mean(arr);
  const s = stddev(arr);
  if (s === 0) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += ((arr[i] - m) / s) ** 4;
  }
  const term1 = (n * (n + 1)) / ((n - 1) * (n - 2) * (n - 3));
  const term2 = (3 * (n - 1) ** 2) / ((n - 2) * (n - 3));
  return term1 * sum - term2;
}

// ═══════════════════════════════════════════════════════════════════
// VALUE FILTERING — Per-metric nullThreshold handling
// ═══════════════════════════════════════════════════════════════════

/**
 * Filter out nulls, NaN, Infinity, and values at or below nullThreshold.
 *
 * Critical distinction: measured zero (value: 0) is NOT the same as
 * missing data (value: null). If nullThreshold is null, zero is valid.
 *
 * @param {Array} values - Raw values array (may contain null, NaN, Infinity)
 * @param {number|null} nullThreshold - Per-metric threshold (null = no filter)
 * @returns {{ validValues: number[], nullCount: number }}
 */
export function filterValues(values, nullThreshold) {
  const valid = [];
  let nullCount = 0;

  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null || v === undefined || typeof v !== 'number' || Number.isNaN(v) || !Number.isFinite(v)) {
      nullCount++;
    } else if (nullThreshold !== null && v <= nullThreshold) {
      nullCount++;
    } else {
      valid.push(v);
    }
  }

  return { validValues: valid, nullCount };
}

// ═══════════════════════════════════════════════════════════════════
// ROUTING LOGIC — Deterministic Decision Tree
// ═══════════════════════════════════════════════════════════════════

/**
 * Select the classification path based on computed statistics.
 *
 * Routing precedence:
 *   1. Divergent — data crosses zero (structural, not distributional)
 *   2. Discrete  — n < requestedClasses (sparse data)
 *   3. Discrete  — all values identical (degenerate)
 *   4. logQuantile — γ₁ > 2.0 OR β₂ > 7.0 (heavy-tailed power-law)
 *   5. linearQuantile — everything else (bounded/normal)
 *
 * Critical: moderate skew (1.0 < |γ₁| ≤ 2.0 with β₂ ≤ 7.0) defaults
 * to linearQuantile. Log transform is ONLY for severe power-law.
 *
 * @param {{ n: number, min: number, max: number, skewness: number, kurtosis: number }} stats
 * @param {{ requestedClasses: number }} config
 * @returns {{ path: string, reason: string }}
 */
export function selectPath(stats, config) {
  // 1. DIVERGENT CHECK FIRST — structural property, not distributional
  if (stats.min < 0 && stats.max > 0) {
    return { path: 'diverging', reason: 'data crosses zero (min < 0 AND max > 0)' };
  }

  // 2. SPARSE DATA — not enough observations for meaningful classification
  if (stats.n < config.requestedClasses) {
    return { path: 'discrete', reason: `n=${stats.n} < k=${config.requestedClasses}, sparse data` };
  }

  // 3. ALL-IDENTICAL — degenerate input
  if (stats.min === stats.max) {
    return { path: 'discrete', reason: 'all values identical (min === max)' };
  }

  // 4. HEAVY-TAIL CHECK — severe power-law distributions
  if (stats.skewness > 2.0 || stats.kurtosis > 7.0) {
    return {
      path: 'logQuantile',
      reason: `γ₁=${stats.skewness.toFixed(2)} > 2.0 or β₂=${stats.kurtosis.toFixed(2)} > 7.0 → heavy-tailed`,
    };
  }

  // 5. DEFAULT — bounded/normal data
  return {
    path: 'linearQuantile',
    reason: `γ₁=${stats.skewness.toFixed(2)}, β₂=${stats.kurtosis.toFixed(2)} within normal range`,
  };
}

// ═══════════════════════════════════════════════════════════════════
// MAIN ENTRY POINT — profileData()
// ═══════════════════════════════════════════════════════════════════

/**
 * Profile a dataset: filter values, compute statistics, route to path.
 *
 * This is the Phase 1 → Phase 2 contract. The output schema is consumed
 * by all three break calculators.
 *
 * @param {Array} values - Raw values (may contain null, NaN, Infinity, etc.)
 * @param {{ nullThreshold?: number|null, requestedClasses?: number, metricId?: string }} config
 * @returns {object} Profile object for Phase 2
 */
export function profileData(values, config = {}) {
  const nullThreshold = config.nullThreshold ?? null;
  const requestedClasses = config.requestedClasses ?? 7;
  const metricId = config.metricId ?? 'unknown';

  // Filter values
  const { validValues, nullCount } = filterValues(values || [], nullThreshold);

  // Handle degenerate cases
  if (validValues.length === 0) {
    return {
      metricId,
      validValues: [],
      nullCount: (values || []).length,
      stats: { n: 0, min: 0, max: 0, mean: 0, median: 0, stddev: 0, skewness: 0, kurtosis: 0 },
      selectedPath: 'discrete',
      reason: 'no valid values after filtering',
      isDegenerate: true,
      isInteger: false,
    };
  }

  // Compute statistics
  const sorted = [...validValues].sort((a, b) => a - b);
  const stats = {
    n: validValues.length,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    mean: mean(validValues),
    median: median(validValues),
    stddev: stddev(validValues),
    skewness: skewness(validValues),
    kurtosis: kurtosis(validValues),
  };

  // Detect integer vs continuous data (for legend formatting in Phase 3)
  const isInteger = validValues.every(v => Number.isInteger(v));

  // Route to path
  const { path, reason } = selectPath(stats, { requestedClasses });

  return {
    metricId,
    validValues,
    nullCount,
    stats,
    selectedPath: path,
    reason,
    isDegenerate: stats.n < 2 || stats.min === stats.max,
    isInteger,
  };
}
